import { getFetcher, postFetcher } from '@/api/fetchers';
import {
  Button,
  Center,
  CheckIcon,
  Checkbox,
  Divider,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
  rem,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import { FaSearch } from 'react-icons/fa';
import useSWR, { mutate } from 'swr';
import { IconX } from '@tabler/icons-react';
import { type OverviewEntityResponses } from '@/schemas/API/GET/overviewEntity/response';
import { type DispatchRequest } from '@/schemas/API/POST/dispatch/request';
import {
  type EmployeePlanExportRequest,
  type ClientPlanExportRequest,
} from '@/schemas/API/POST/exports/request';
import { modals } from '@mantine/modals';
import { downloadFileWithBearerToken } from '@/utils/functions';

interface ReportsModalProps {
  title?: string;
  planId: number;
  serviceId: number;
  reportType: 'PLAN' | 'REPORT';
}

interface Endpoints {
  sourceEnpoint: string;
  sendEndpoint: string;
}

export const ReportsModal: React.FC<ReportsModalProps> = props => {
  const { title, planId, serviceId, reportType } = props;
  const [page, setPage] = useState(1);
  const [nameFilter, setNameFilter] = useState('');
  const [modFilter, setModFilter] = useState('');
  const [targetGroup, setTargetGroup] = useState('CLIENT');
  const [selectedIDs, setSelectedIDs] = useState<number[]>([]);

  const { data: modificationStates } = useSWR<Record<string, string>>(
    '/mappings/modification-states',
    getFetcher
  );

  const [modSelectorData, setModSelectorData] = useState([
    { value: '', label: 'Všechny' },
  ]);

  useEffect(() => {
    if (modificationStates) {
      const modData = Object.keys(modificationStates).map(key => ({
        value: key,
        label: modificationStates[key],
      }));
      setModSelectorData(prev => [...prev, ...modData]);
    }
  }, [modificationStates]);

  const targetGroupSelectorData = [
    { value: 'CLIENT', label: 'Klientům' },
    { value: 'EMPLOYEE', label: 'Zaměstnancům' },
  ];

  const computeEndpoints = (reportType: string): Endpoints => {
    let sourceEnpoint = '';
    let sendEndpoint = '';

    switch (targetGroup) {
      case 'CLIENT':
        sourceEnpoint = reportType === 'PLAN' ? 'CLIENT_PLAN' : 'BILLING';
        sendEndpoint = reportType === 'PLAN' ? 'client-plans' : 'billings';
        break;
      case 'EMPLOYEE':
        sourceEnpoint = reportType === 'PLAN' ? 'EMPLOYEE_SCHEDULE' : 'REPORT';
        sendEndpoint = reportType === 'PLAN' ? 'employee-schedules' : 'reports';
        break;
    }

    return {
      sourceEnpoint,
      sendEndpoint,
    };
  };

  const { sourceEnpoint, sendEndpoint } = computeEndpoints(reportType);

  // add sorting by name
  const endpoint = `/services/${serviceId}/plans/${planId}/overview`;
  const query = `page=${page}&size=${10}&query=${nameFilter}&mod=${modFilter}&entity=${sourceEnpoint}`;
  const urlData = `${endpoint}?${query}`;
  const urlExport = `/exports/plans/${planId}/${sendEndpoint}`;
  const urlDispatch = `/dispatches/plans/${planId}/${sendEndpoint}`;

  const { data: pagableData, error } = useSWR<OverviewEntityResponses>(
    urlData,
    getFetcher
  );
  const data = pagableData?.content;

  const handleChcekboxChange = (id: number) => {
    if (selectedIDs.includes(id)) {
      setSelectedIDs(selectedIDs.filter(item => item !== id));
    } else {
      setSelectedIDs([...selectedIDs, id]);
    }
  };

  const allChecked = (): boolean => {
    if (!data || data.length === 0) {
      return false;
    }

    return data.every(item => selectedIDs.includes(item.entityId));
  };

  const selected = selectedIDs.length > 0;

  const handleChcekAllChange = () => {
    if (!data) {
      return;
    }

    if (allChecked()) {
      setSelectedIDs(
        selectedIDs.filter(
          item => !data.map(item => item.entityId).includes(item)
        )
      );
    } else {
      const newSelectedIDs = [
        ...selectedIDs,
        ...data.map(item => item.entityId),
      ];
      const uniqueSelectedIDs = Array.from(new Set(newSelectedIDs));
      setSelectedIDs(uniqueSelectedIDs);
    }
  };

  const handleDownload = async (
    body: EmployeePlanExportRequest | ClientPlanExportRequest
  ) => {
    try {
      const response = await postFetcher(urlExport, body);
      await pollExportStatus(response.exportId);

      notifications.show({
        message: `Soubor byl stažen.`,
        color: 'green',
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
      await new Promise(resolve => setTimeout(resolve, 500)); //  TODO: nechat nastavit db, aby ne data neposilala dokud nebude hotova zmena
      mutate(urlData);
    } catch (error) {
      notifications.show({
        message: 'Požadavek na nepodařilo zpracovat. Zkuste to prosím později.',
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
    }
  };

  const pollExportStatus = async (exportId: string) => {
    const getExportStatus = async () => {
      const exportStatus = await getFetcher(`/exports/${exportId}`);
      if (exportStatus.state === 'FINISHED') {
        await downloadFileWithBearerToken(exportStatus.url);
      } else if (exportStatus.state !== 'RUNNING') {
        throw new Error('Export failed');
      }
      return exportStatus.state;
    };

    let state = 'RUNNING';
    while (state === 'RUNNING') {
      await new Promise(resolve => setTimeout(resolve, 1000));
      state = await getExportStatus();
    }
  };

  const handleDownloadWithoutEmails = () => {
    const body = {
      ids: null,
      withoutMailOnly: true,
      all: false,
    };

    handleDownload(body);
  };

  const handleDownloadAllOrSelected = () => {
    if (!selected) {
      const body = {
        ids: null,
        withoutMailOnly: false,
        all: true,
      };

      handleDownload(body);
      return;
    }

    const body = {
      ids: selectedIDs,
      withoutMailOnly: false,
      all: false,
    };
    handleDownload(body);
  };

  const handleDownloadOne = (id: number) => {
    const body = {
      ids: [id],
      withoutMailOnly: false,
      all: false,
    };
    handleDownload(body);
  };

  const handleDispatch = async (body: DispatchRequest) => {
    try {
      const response = await postFetcher(urlDispatch, body);
      await pollDispatchStatus(response.dispatchId);
      mutate(urlData);
    } catch (error) {
      notifications.show({
        message: 'Požadavek na odeslání se nepodařilo zpracovat.',
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
      console.error(error);
    }
  };

  const pollDispatchStatus = async (dispatchId: string) => {
    const getDispatchStatus = async () => {
      const dispatchStatus = await getFetcher(`/dispatches/${dispatchId}`);
      if (dispatchStatus.state === 'FINISHED') {
        notifications.show({
          message: `Hotovo. Všechny emaily (celkem ${dispatchStatus.successCount}) se podařilo odeslat.`,
          color: 'green',
          icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
        });
      } else if (dispatchStatus.state === 'FINISHED_WITH_ERRORS') {
        const failedEmails = dispatchStatus.fail
          .map(
            (failItem: any) =>
              `${failItem.firstName} ${failItem.lastName} (${failItem.email})`
          )
          .join('\n');
        notifications.show({
          message: `Některé emaily se nepodařilo odeslat. Zde je jejich seznam:\n${failedEmails}`,
          color: 'yellow',
          icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
          autoClose: false,
        });
      } else if (dispatchStatus.state === 'FAILED') {
        notifications.show({
          message:
            'Požadavek na odeslání se nepodařilo zpracovat. Zkuste to prosím později.',
          color: 'red',
          icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
        });
      }

      return dispatchStatus.state;
    };

    let state = 'RUNNING';
    while (state === 'RUNNING') {
      await new Promise(resolve => setTimeout(resolve, 1000));
      state = await getDispatchStatus();
    }
  };

  const handleDispatchAllOrSelected = () => {
    if (!selected && targetGroup === 'EMPLOYEE') {
      const body = {
        ids: null,
        all: true,
        withMailOnly: null,
      };

      handleDispatch(body);
      return;
    }

    if (!selected) {
      const body = {
        ids: null,
        all: null,
        withMailOnly: true,
      };

      handleDispatch(body);
      return;
    }

    const body = {
      ids: selectedIDs,
      all: false,
      withMailOnly: null,
    };
    handleDispatch(body);
  };

  const doubleConfirmModal = async (): Promise<boolean> => {
    return new Promise(resolve => {
      modals.openConfirmModal({
        labels: { confirm: 'ANO', cancel: 'NE' },
        confirmProps: { variant: 'filled', color: 'red' },
        cancelProps: { variant: 'outline', color: 'blue' },
        centered: true,
        children: (
          <Text fw={700}>
            Přejete si provést odeslání i když má osoba aktuální údaje?
          </Text>
        ),
        onConfirm: () =>
          modals.openConfirmModal({
            labels: { confirm: 'ANO', cancel: 'NE' },
            confirmProps: { variant: 'filled', color: 'red' },
            cancelProps: { variant: 'outline', color: 'blue' },
            centered: true,
            children: <Text fw={700}>Opravdu si přejete akci provést?</Text>,
            onConfirm: () => {
              resolve(true);
            },
          }),
      });
    });
  };

  const checkActualState = (id: number): boolean => {
    if (!data) {
      return false;
    }

    const item = data.find(item => item.entityId === id);

    if (!item) {
      return false;
    }

    return item.modState === 'ACTUAL';
  };

  const handleDispatchOne = async (id: number) => {
    if (checkActualState(id)) {
      if (!(await doubleConfirmModal())) {
        return;
      }
    }

    const body = {
      ids: [id],
      all: false,
      withMailOnly: null,
    };
    handleDispatch(body);
  };

  const getDate = (dateStr: string) => {
    const date = dayjs(dateStr);

    const fullDate = date.format('DD.MM.YYYY HH:mm:ss');

    return (
      <Tooltip label={fullDate}>
        <Text>{date.format('DD.MM.YYYY')}</Text>
      </Tooltip>
    );
  };

  const getStateIdentifier = (state: string) => {
    if (!modificationStates) {
      return null;
    }

    let color = 'lightgray';
    const tooltip = modificationStates[state] ?? '';

    switch (state) {
      case 'ACTUAL':
        color = '#00c853';
        break;
      case 'OUTDATED':
        color = '#d50000';
        break;
      case 'MODIFIED':
        color = '#ffab00';
        break;
      case 'NOT_DISPATCHED':
        color = '#a9a9a9';
        break;
    }

    return (
      <Tooltip label={tooltip}>
        <div
          style={{
            width: '25px',
            height: '25px',
            borderRadius: '50%',
            backgroundColor: color,
          }}
        ></div>
      </Tooltip>
    );
  };

  const getTable = () => {
    if (error) {
      return (
        <Stack
          align="center"
          justify="center"
          style={{ height: '550px' }}
          gap="xs"
        >
          <Text>Nepodařilo se načíst data.</Text>
          <Text>Žádné záznamy nebyly pro aktuální službu a plán nalezeny.</Text>
        </Stack>
      );
    }

    if (!data) {
      return (
        <Stack
          align="center"
          justify="center"
          style={{ height: '550px' }}
          gap="xs"
        >
          <Text>Načítání dat...</Text>
          <Loader />
        </Stack>
      );
    }

    if (data.length === 0) {
      return (
        <Stack
          align="center"
          justify="center"
          style={{ height: '550px' }}
          gap="xs"
        >
          <Text>Žádné záznamy nebyly pro aktuální službu a plán nalezeny.</Text>
        </Stack>
      );
    }

    return (
      <div>
        <Stack gap={'xs'}>
          <Group justify="space-between">
            <Group align="center" style={{ marginLeft: '10px' }}>
              <Checkbox
                onChange={handleChcekAllChange}
                checked={allChecked()}
              />
              <Text>Označit všechny na aktuální stránce</Text>
            </Group>
            <Group justify="flex-end">
              {selected && (
                <Text>
                  Vybrané: {selectedIDs.length} / {pagableData?.totalElements}
                </Text>
              )}
            </Group>
          </Group>
          <Divider />

          <Table highlightOnHover>
            <Table.Tbody>
              {data.map((item, index) => (
                <Table.Tr key={index}>
                  <Table.Td>
                    <Group justify="flex-start" align="center">
                      <Checkbox
                        value={item.entityId}
                        onChange={() => handleChcekboxChange(item.entityId)}
                        checked={selectedIDs.includes(item.entityId)}
                      />
                      <Text>
                        {item.details?.firstName} {item?.details?.lastName}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group justify="flex-end" align="center">
                      {getDate(item.lastUpdateTime)}
                      {getStateIdentifier(item.modState)}
                      <Button
                        disabled={item.details?.email === null}
                        onClick={async () => handleDispatchOne(item.entityId)}
                      >
                        Odeslat mailem
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDownloadOne(item.entityId)}
                      >
                        Stáhnout PDF
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      </div>
    );
  };

  const getTitle = () => {
    return (
      title ??
      (reportType === 'REPORT' ? 'Odesílání výkazů' : 'Odesílání plánů')
    );
  };

  const getConent = () => {
    return (
      <Stack style={{ height: '81vh' }} justify="space-between">
        <Stack>
          <Group justify="flex-start" align="center">
            <Text fw={700} size="large">
              {getTitle()}
            </Text>
            <Select
              data={targetGroupSelectorData}
              placeholder="komu"
              defaultValue={targetGroupSelectorData[0].value}
              onChange={val => {
                setTargetGroup(val ?? '');
                setPage(1);
                setSelectedIDs([]);
              }}
              allowDeselect={false}
            />
          </Group>

          <Group justify="space-between">
            <TextInput
              placeholder="Vyhledat"
              onChange={e => {
                setNameFilter(e.currentTarget.value);
                setPage(1);
              }}
              style={{ width: '64%' }}
              leftSection={<FaSearch />}
            />
            <Select
              data={modSelectorData}
              placeholder="Stav"
              defaultValue={modSelectorData[0].value}
              onChange={val => {
                setModFilter(val ?? '');
                setSelectedIDs([]);
                setPage(1);
              }}
              allowDeselect={false}
            />
          </Group>
          {getTable()}
        </Stack>
        <Stack>
          <Center>
            <Pagination
              total={pagableData?.totalPages ?? 1}
              value={page}
              onChange={setPage}
              withEdges
              size="sm"
            />
          </Center>
          <Group justify="flex-end">
            <Button
              variant="outline"
              disabled={selected || !data || data.length === 0}
              onClick={handleDownloadWithoutEmails}
            >
              Stáhnout jen bez emailů
            </Button>
            <Button
              onClick={handleDispatchAllOrSelected}
              disabled={!data || data.length === 0}
            >
              Odeslat {selected ? 'vybraným' : 'všem'}
            </Button>
            <Button
              onClick={handleDownloadAllOrSelected}
              disabled={!data || data.length === 0}
            >
              Stáhnout {selected ? 'vybrané' : 'všechny'}
            </Button>
          </Group>
        </Stack>
      </Stack>
    );
  };

  return getConent();
};

export const openReportsModal = (
  planId: number | undefined,
  serviceId: number | undefined,
  reportType: 'PLAN' | 'REPORT',
  title?: string
) => {
  if (!planId || !serviceId) {
    notifications.show({
      title: 'Nepodařilo se otevřít okno.',
      message: 'Není vybrán plán nebo služba.',
      color: 'red',
      icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
    });
    return;
  }

  modals.open({
    size: 'xl',
    children: (
      <ReportsModal
        title={title}
        planId={planId}
        serviceId={serviceId}
        reportType={reportType}
      />
    ),
  });
};
